import EditIcon from "@mui/icons-material/Edit";
import VerticalAlignCenterIcon from "@mui/icons-material/VerticalAlignCenter";
import { Collapse, IconButton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import React, { useMemo } from "react";

import { getShowOptions, renderPath } from "../utils/archiverUtils";
import {
  ComboTextFieldLeft,
  ComboTextFieldRight,
  HeightAdjustedButton,
  HeightAdjustedTextField,
  StyledCardContent,
} from "../utils/StyledComponents";

import CandidateActions from "./CandidateActions";
import SelectForm from "./SelectForm";

export default function SeriesCandidate({
  lightBackground,
  candidate,
  candidateRoot,
  shows,
  actionTypes,
  onActionIconClick,
  handleEditClick,
  onCandidateTextFieldChange,
  onCandidateUpdate,
  onAdditionalTitleChange,
  onSelectOptionChange,
}) {
  const isEditOpen = useMemo(() => candidate.isEditState ?? false, [candidate.isEditState]);
  const showActions = useMemo(
    () => candidateRoot.archiveActionType === null,
    [candidateRoot.archiveActionType],
  );
  const series = useMemo(
    () => shows.find((s) => s.id === candidate.seriesId),
    [shows, candidate.seriesId],
  );

  const multiCount = candidate.multiEpisodeCount ?? 1;

  const renderMultiEpisodeNames = () => {
    if (candidate.episodeNumber === null) return null;
    const blocks = [];

    for (let i = 0; i < multiCount - 1; i++) {
      const additionalEpisode = candidate.additionalEpisodes?.[i];
      blocks.push(
        <Grid key={i}>
          <Typography
            variant="subtitle2"
            color="textPrimary"
            component="p"
            style={{ overflowWrap: "break-word" }}
          >
            {`Title ${candidate.episodeNumber + i + 1}`}
          </Typography>
          <Typography
            variant="body1"
            color="textPrimary"
            component="p"
            style={{ overflowWrap: "break-word" }}
          >
            {additionalEpisode?.name ?? ""}
          </Typography>
        </Grid>,
      );
    }

    return blocks;
  };

  const renderMultiEpisodeFields = () => {
    if (candidate.episodeNumber === null) return null;
    const fields = [];

    for (let i = 0; i < multiCount - 1; i++) {
      const additionalEpisode = candidate.additionalEpisodes?.[i];
      fields.push(
        <Grid size={12} key={i}>
          <HeightAdjustedTextField
            value={additionalEpisode?.name ?? ""}
            onChange={onAdditionalTitleChange}
            id={i.toString()}
            name={candidate.path}
            label={`Title Episode ${candidate.episodeNumber + i + 1}`}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>,
      );
    }

    return fields;
  };

  const episodeLabel = multiCount > 1 ? "Episodes" : "Episode";
  const episodeRange =
    candidate.episodeNumber > 0
      ? `${candidate.episodeNumber}${multiCount > 1 ? `-${candidate.episodeNumber + multiCount - 1}` : ""}`
      : "";

  const titleLabel = multiCount > 1 ? `Title ${candidate.episodeNumber ?? ""}` : "Title";

  return (
    <StyledCardContent isEven={lightBackground}>
      <Grid container spacing={2} alignItems="center">
        {renderPath(candidate, candidateRoot)}
        <Grid size={{ xs: 12, md: 4 }}>
          {showActions ? (
            <CandidateActions
              candidate={candidate}
              actionTypes={actionTypes}
              onActionIconClick={onActionIconClick}
            />
          ) : null}
        </Grid>
      </Grid>

      {/* Read-only summary view */}
      <Collapse in={!isEditOpen} timeout="auto" unmountOnExit>
        <br />
        <Grid container spacing={2} justifyContent="flex-start">
          <Grid>
            <IconButton name={candidate.path} onClick={handleEditClick}>
              <EditIcon />
            </IconButton>
          </Grid>

          <Grid>
            <Typography
              variant="subtitle2"
              color="textPrimary"
              component="p"
              style={{ overflowWrap: "break-word" }}
            >
              Series
            </Typography>
            <Typography
              variant="body1"
              color="textPrimary"
              component="p"
              style={{ overflowWrap: "break-word" }}
            >
              {series?.name ?? ""}
            </Typography>
          </Grid>

          <Grid>
            <Typography
              variant="subtitle2"
              color="textPrimary"
              component="p"
              style={{ overflowWrap: "break-word" }}
            >
              Season
            </Typography>
            <Typography
              variant="body1"
              color="textPrimary"
              component="p"
              style={{ overflowWrap: "break-word" }}
            >
              {candidate.seasonNumber ?? ""}
            </Typography>
          </Grid>

          <Grid>
            <Typography
              variant="subtitle2"
              color="textPrimary"
              component="p"
              style={{ overflowWrap: "break-word" }}
            >
              {episodeLabel}
            </Typography>
            <Typography
              variant="body1"
              color="textPrimary"
              component="p"
              style={{ overflowWrap: "break-word" }}
            >
              {episodeRange}
            </Typography>
          </Grid>

          <Grid>
            <Typography
              variant="subtitle2"
              color="textPrimary"
              component="p"
              style={{ overflowWrap: "break-word" }}
            >
              {titleLabel}
            </Typography>
            <Typography
              variant="body1"
              color="textPrimary"
              component="p"
              style={{ overflowWrap: "break-word" }}
            >
              {candidate?.name ?? ""}
            </Typography>
          </Grid>

          {renderMultiEpisodeNames()}
        </Grid>
      </Collapse>

      {/* Edit view */}
      <Collapse in={isEditOpen} timeout="auto" unmountOnExit>
        <br />
        <Grid container spacing={2} justifyContent="space-between">
          <Grid size={{ xs: 2, sm: 1, md: "auto" }}>
            <IconButton
              name={candidate.path}
              onClick={handleEditClick}
              aria-expanded={isEditOpen}
              aria-label="edit"
            >
              <VerticalAlignCenterIcon />
            </IconButton>
          </Grid>

          <Grid size={{ xs: 10, sm: 11, md: 5, lg: 3 }}>
            <SelectForm
              name={candidate.path}
              id="seriesId"
              value={
                candidateRoot.seriesId !== null ? candidateRoot.seriesId : (candidate.seriesId ?? 0)
              }
              onChange={onSelectOptionChange}
              label="Series"
              options={getShowOptions(shows)}
              disabled={candidateRoot.seriesId !== null}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
            <Grid container spacing={0}>
              <Grid size={6}>
                <ComboTextFieldLeft
                  value={
                    candidateRoot.seasonNumber !== null
                      ? candidateRoot.seasonNumber
                      : (candidate.seasonNumber ?? "")
                  }
                  disabled={candidateRoot.seasonNumber !== null}
                  onChange={onCandidateTextFieldChange}
                  id="seasonNumber"
                  name={candidate.path}
                  type="number"
                  label="Season"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={6}>
                <ComboTextFieldRight
                  value={candidate.episodeNumber ?? ""}
                  onChange={onCandidateTextFieldChange}
                  id="episodeNumber"
                  name={candidate.path}
                  type="number"
                  label="Episode"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={{ xs: 6, sm: 3, md: 2, lg: 2 }}>
            <HeightAdjustedTextField
              value={candidate.multiEpisodeCount}
              onChange={onCandidateTextFieldChange}
              id="multiEpisodeCount"
              name={candidate.path}
              type="number"
              label="Count"
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 6, sm: 3, md: 6, lg: 2 }} />

          <Grid size={{ xs: 12, sm: 12, md: 6, lg: 2 }}>
            <HeightAdjustedButton
              variant="contained"
              onClick={onCandidateUpdate}
              color="primary"
              size="large"
              name={candidate.path}
              fullWidth
            >
              Update
            </HeightAdjustedButton>
          </Grid>

          <Grid size={12}>
            <HeightAdjustedTextField
              value={candidate.name}
              onChange={onCandidateTextFieldChange}
              id="name"
              name={candidate.path}
              label={`Title${multiCount > 1 ? ` Episode ${candidate.episodeNumber}` : ""}`}
              variant="outlined"
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {renderMultiEpisodeFields()}
        </Grid>
      </Collapse>
    </StyledCardContent>
  );
}
